import { flow, pipe } from 'fp-ts/lib/function';
import { Group } from '@/entities/group';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  setBody,
  setQuery,
  Versioned,
} from '../..';
import { addVersionForUpdate } from '../../request';
import { encodeGroupQuery, GroupQuery } from '@/services/api/resources/group/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';

const baseUrl = '/v2/groups';
const filterUrl = '/v2/filters/groups';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;

export const list = (query: GroupQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeGroupQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = () => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const create = (group: Group) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(group))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, group: Versioned<Group>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(group.version)),
  map(setBody(formCodec.encode(group.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);
